import {
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Field, Form, FieldArray, FormikErrors } from "formik";
import Sidebar from "./CourseSidebar";
import { useSnackbar } from "../../../../components/utils/SnackBar";
import color from "../../../../components/utils/Colors";
import { SaveOutlined } from "@mui/icons-material";
import FileUploadModal from "../../../../components/modal/FileUploadModal";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { courseContentvalidation } from "../../../../components/schema/schema";
import {
  createCourseContent,
  deleteDataFromTable,
  getDataFromTable,
  updateRecordById,
  uploadCourseContentVideo,
} from "../../../../services/services";
import { jwtDecode } from "jwt-decode";
import { useUser } from "../../../../services/Context/UserContext";

interface Lecture {
  id?: string | number;
  title: string;
  description: string;
  lectureVideo: File | string | null;
}

interface Section {
  title: string;
  description: string;
  lectures: Lecture[];
}

const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decoded: any = jwtDecode(token);
      return decoded.id || decoded.userId || decoded.sub;
    }
  } catch (err) {
    console.error("Error decoding token:", err);
  }
  return null;
};

const CourseContent = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();
  // Track which section indices are currently being saved individually
  const [savingSection, setSavingSection] = useState<number | null>(null);

  const courseId = (location.state as any)?.courseId;
  const [open, setOpen] = useState(false);
  const { user } = useUser();

  const [formValues, setFormValues] = useState({
    sections: [
      {
        title: "",
        description: "",
        lectures: [{ title: "", description: "", lectureVideo: null }],
      },
    ],
  });

  useEffect(() => {
    const fetchContent = async () => {
      if (courseId) {
        try {
          const res = await getDataFromTable("courseContent", { courseId });
          if (res?.data?.data && res.data.data.length > 0) {
            const lectures = res.data.data.map((l: any) => ({
              id: l.id,
              title: l.lectureName || "",
              description: l.lectureDesc || "",
              lectureVideo: l.lectureVideo,
            }));

            // Put all fetched lectures into one section since section title is not stored in db currently
            setFormValues({
              sections: [
                {
                  title: "Course Lectures",
                  description: "",
                  lectures,
                },
              ],
            });
          }
        } catch (error) {
          console.error("Failed to fetch course content:", error);
          // don't show snackbar to not annoy user if content is just empty
        }
      }
    };
    fetchContent();
  }, [courseId]);
  const handleAddSection = (
    sections: Section[],
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const lastSection = sections[sections.length - 1];
    if (lastSection?.title.trim()) {
      setFieldValue("sections", [
        ...sections,
        {
          title: "",
          description: "",
          lectures: [{ title: "", description: "", lectureVideo: null }],
        },
      ]);
    } else {
      alert(
        "Please fill the previous section title before adding a new section.",
      );
    }
  };

  const handleAddLecture = (
    sectionIndex: number,
    sections: Section[],
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const lectures = sections[sectionIndex].lectures;
    const lastLecture = lectures[lectures.length - 1];
    if (lastLecture?.title.trim()) {
      const updated = sections.map((s, i) =>
        i === sectionIndex
          ? {
              ...s,
              lectures: [
                ...s.lectures,
                {
                  title: "",
                  description: "",
                  lectureVideo: null,
                },
              ],
            }
          : s,
      );
      setFieldValue("sections", updated);
    } else {
      alert(
        "Please fill the previous lecture title before adding a new lecture.",
      );
    }
  };
  const handleRemoveLecture = async (
    sectionIndex: number,
    lectureIndex: number,
    sections: Section[],
    setFieldValue: (field: string, value: any) => void,
  ) => {
    if (sections[sectionIndex].lectures.length === 1) {
      alert("Each section must have at least one lecture.");
      return;
    }

    const lecture = sections[sectionIndex].lectures[lectureIndex];

    // ← NEW: delete from DB if it has an id
    if (lecture.id) {
      try {
        await deleteDataFromTable("courseContent", lecture.id);
      } catch (err: any) {
        alert(
          err.response?.data?.message ||
            "Failed to delete lecture from server.",
        );
        return; // don't remove from UI if server delete failed
      }
    }

    const updated = sections.map((s, i) =>
      i === sectionIndex
        ? { ...s, lectures: s.lectures.filter((_, li) => li !== lectureIndex) }
        : s,
    );
    setFieldValue("sections", updated);
  };

  // ── Save a single section (does NOT submit the whole form) ─────────────────
  const handleSaveSection = async (
    sectionIndex: number,
    sections: Section[],
    startOrder: number,
  ) => {
    const section = sections[sectionIndex];

    if (!section.title.trim()) {
      alert("Please enter a section title before saving.");
      return;
    }

    const createdBy = getUserIdFromToken();
    if (!createdBy) {
      alert("Unable to determine user. Please log in again.");
      return;
    }

    setSavingSection(sectionIndex);
    let lectureOrder = startOrder;

    try {
      for (const lecture of section.lectures) {
        if (!lecture.title.trim()) continue;

        const lectureData = {
          courseId,
          lectureName: lecture.title,
          lectureDesc: lecture.description || "",
          lectureOrder: lectureOrder++,
          createdBy: parseInt(createdBy) || createdBy,
          status: "draft",
          contentType: "lecture",
          teachersName: user?.name || "",
        };

        let lectureId = lecture.id;

        if (lecture.id) {
          await updateRecordById("courseContent", lecture.id, lectureData);
        } else {
          const response = await createCourseContent(lectureData as any);

          // Adjust this according to your API response
          lectureId = response?.data?.data?.id;
        }

        // Upload lecture video
        if (lecture.lectureVideo && lectureId) {
          const formData = new FormData();
          formData.append("lectureVideo", lecture.lectureVideo);

          await uploadCourseContentVideo(lectureId, formData, "lecture-videos");
        }
      }

      alert(`Section ${sectionIndex + 1} saved successfully.`);
    } catch (err: any) {
      alert(
        err.response?.data?.message ||
          err.message ||
          "Failed to save section. Please try again.",
      );
      console.error("Section save error:", err);
    } finally {
      setSavingSection(null);
    }
  };

  const handleFormSubmit = async (values: { sections: Section[] }) => {
    setLoading(true);

    const createdBy = getUserIdFromToken();

    try {
      let lectureOrder = 1;

      for (const section of values.sections) {
        if (!section.title.trim()) continue;

        for (const lecture of section.lectures) {
          if (!lecture.title.trim()) continue;

          const lectureData = {
            courseId,
            lectureName: lecture.title,
            lectureDesc: lecture.description || "",
            lectureOrder: lectureOrder++,
            createdBy: parseInt(createdBy) || createdBy,
            status: "draft",
            contentType: "lecture",
            teachersName: user?.name || "",
          };

          let lectureId = lecture.id;

          if (lecture.id) {
            await updateRecordById("courseContent", lecture.id, lectureData);
          } else {
            const response = await createCourseContent(lectureData as any);

            // Get the newly created lecture ID from API response
            lectureId = response?.data?.data?.id;
          }

          // Upload video if selected
          if (lecture.lectureVideo && lectureId) {
            const formData = new FormData();
            formData.append("lectureVideo", lecture.lectureVideo);

            await uploadCourseContentVideo(
              lectureId,
              formData,
              "lectureVideo", // optional folder name
            );
          }
        }
      }

      navigate("/course-landingPage", {
        state: { courseId },
      });
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          err.message ||
          "An error occurred while creating course content. Please try again.",
      );
      console.error("Course content creation error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Box
      sx={{
        display: "flex",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: isSmallScreen ? "5px" : "30px",
        gap: "1%",
        marginBottom: "0px",
        paddingBottom: "5%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      {/* <Sidebar /> */}

      <Box
        sx={{
          marginTop: isSmallScreen ? "25px" : "0px",
          flex: 1,
          pl: "3%",
          borderRadius: "16px",
        }}
      >
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={courseContentvalidation}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            handleChange,
            touched,
            errors,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    background: color.textColor,
                    textAlign: "center",
                    color: "white",
                    width: "fit-content",
                    margin: "auto",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    mb: 2,
                  }}
                >
                  Create Content / Modules
                </Typography>
                <Button
                  onClick={handleOpen}
                  variant="contained"
                  id="button"
                  color="primary"
                  sx={{ borderRadius: "20px", ml: "auto" }}
                >
                  Upload In Bulk
                </Button>
                <FileUploadModal open={open} handleClose={handleClose} />
              </Box>

              <FieldArray name="sections">
                {() => (
                  <>
                    {values.sections.map((section, sectionIndex) => {
                      // Calculate the lecture order offset for this section
                      // so individual saves don't collide with others
                      const orderOffset =
                        values.sections
                          .slice(0, sectionIndex)
                          .reduce((acc, s) => acc + 1 + s.lectures.length, 0) +
                        1;

                      return (
                        <Box
                          key={sectionIndex}
                          sx={{
                            border: "solid 2px",
                            borderColor: color.borderColor,
                            padding: "20px",
                            borderRadius: "8px",
                            backgroundColor: "white",
                            mb: 2,
                            overflow: "hidden",
                          }}
                        >
                          {/* Section header */}
                          <div
                            style={{
                              boxShadow: "0px 10px 25px #00000017",
                              padding: "20px",
                              margin: "-20px",
                              marginBottom: "15px",
                            }}
                          >
                            <Typography
                              sx={{
                                color: color.textColor,
                                mb: 2,
                                fontWeight: "bold",
                              }}
                            >
                              {`Section ${sectionIndex + 1}: ${section.title}`}
                            </Typography>
                            <TextField
                              className="input-root2"
                              name={`sections[${sectionIndex}].title`}
                              placeholder="Section Title"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={section.title}
                              fullWidth
                              required
                              sx={{ mb: 2 }}
                              error={
                                touched.sections?.[sectionIndex]?.title &&
                                Boolean(
                                  (
                                    errors.sections as
                                      | FormikErrors<Section[]>
                                      | undefined
                                  )?.[sectionIndex]?.title,
                                )
                              }
                              helperText={
                                touched.sections?.[sectionIndex]?.title &&
                                (
                                  errors.sections as
                                    | FormikErrors<Section[]>
                                    | undefined
                                )?.[sectionIndex]?.title
                              }
                            />
                          </div>

                          {/* Lectures */}
                          {section.lectures.map((lecture, lectureIndex) => (
                            <Box
                              key={lectureIndex}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                mb: 2,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: color.textColor,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {`Lecture ${lectureIndex + 1}: ${lecture.title}`}
                                </Typography>
                                <IconButton
                                  size="small"
                                  sx={{ color: color.firstColor }}
                                  onClick={() =>
                                    handleRemoveLecture(
                                      sectionIndex,
                                      lectureIndex,
                                      values.sections,
                                      setFieldValue,
                                    )
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>

                              <Field
                                className="input-root2"
                                as={TextField}
                                name={`sections[${sectionIndex}].lectures[${lectureIndex}].title`}
                                placeholder="Lecture Title"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={lecture.title}
                                required
                                fullWidth
                                sx={{ mb: 1 }}
                              />
                              <Field
                                className="input-root3"
                                as={TextField}
                                name={`sections[${sectionIndex}].lectures[${lectureIndex}].description`}
                                placeholder="Lecture Description"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={lecture.description}
                                fullWidth
                                multiline
                                rows={3}
                              />
                              {lecture.lectureVideo ? (
                                <video
                                  controls
                                  width="full"
                                  src={
                                    lecture.lectureVideo instanceof File
                                      ? URL.createObjectURL(
                                          lecture.lectureVideo,
                                        )   
                                      : process.env.REACT_APP_PUBLIC_BASE_URL +
                                        lecture.lectureVideo 
                                  }
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No Video chosen
                                </Typography>
                              )}
                              <input
                                type="file"
                                accept="video/*"
                                onChange={(e) => {
                                  const file =
                                    e.currentTarget.files?.[0] || null;
                                  setFieldValue(
                                    `sections.${sectionIndex}.lectures.${lectureIndex}.lectureVideo`,
                                    file,
                                  );
                                }}
                              />
                            </Box>
                          ))}

                          {/* Section actions */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "20px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              id="button"
                              startIcon={<AddIcon />}
                              sx={{ ml: "auto" }}
                              onClick={() =>
                                handleAddLecture(
                                  sectionIndex,
                                  values.sections,
                                  setFieldValue,
                                )
                              }
                            >
                              Add Lecture
                            </Button>

                            <Button
                              type="button"
                              id="button"
                              startIcon={
                                savingSection === sectionIndex ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SaveOutlined />
                                )
                              }
                              disabled={savingSection === sectionIndex}
                              sx={{ ml: "auto" }}
                              onClick={() =>
                                handleSaveSection(
                                  sectionIndex,
                                  values.sections,
                                  orderOffset,
                                )
                              }
                            >
                              {savingSection === sectionIndex
                                ? "Saving…"
                                : "Save Section"}
                            </Button>
                          </div>
                        </Box>
                      );
                    })}

                    <Button
                      id="button"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      sx={{ width: "100%" }}
                      onClick={() =>
                        handleAddSection(values.sections, setFieldValue)
                      }
                    >
                      Add Section
                    </Button>
                  </>
                )}
              </FieldArray>

              {/* Final submit — saves everything and navigates to Landing Page */}
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  id="button"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} />
                      Creating…
                    </Box>
                  ) : (
                    "Save & Next"
                  )}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default CourseContent;
